import { z } from 'zod';

import { type AnswerReading, readAnswerDocument, unfencedAnswer } from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import {
  BRIEF_ANSWER_EXAMPLE,
  BRIEF_ANSWER_RULES,
  type BriefAnswer,
  type BriefHandles,
  type BriefRqHandle,
  briefAnswerWireShape,
  readBriefAnswerValue,
  rqThemeHandles,
} from './brief-answer.ts';
import { briefAnswerOf } from './brief-answer-of.ts';
import { handleNumber } from './brief-compose.ts';
import { type RichText, briefToken } from './brief-counts.ts';
import type { BriefContent } from './brief.ts';
import { followUpAnyOfToken, readFollowUpNote } from './brief-follow-ups.ts';

/** The sentence a fixture answer leads with for one of a research question's polls. */
const pollSentence = (handles: BriefHandles, handle: string): string => {
  const poll = handles.polls.get(handle);
  const top = (poll?.options ?? []).reduce(
    (best, option, index) => (option.count > (poll?.options[best]?.count ?? -1) ? index : best),
    0,
  );
  const at = { poll: handleNumber(handle), option: top + 1 };
  return `${briefToken({ kind: 'count', ...at })} of the ${briefToken({ kind: 'of', poll: at.poll })} people who answered chose ${briefToken({ kind: 'option', ...at })}.`;
};

/** A research question's first poll anybody answered. */
const answeredPoll = (handles: BriefHandles, rq: BriefRqHandle): string | undefined =>
  rq.polls.find((handle) => (handles.polls.get(handle)?.respondents ?? 0) > 0);

/**
 * The themes a fixture's whole-study answer counts across: the first theme of each research question
 * that has one, so the count reaches across questions where it can, then any others in order, two
 * in all. Fewer than two when the study has fewer.
 */
const themesAcrossQuestions = (handles: BriefHandles): string[] => {
  const firsts = handles.rqs.flatMap((rq) => rqThemeHandles(rq).slice(0, 1));
  const rest = [...handles.themes.keys()].filter((handle) => !firsts.includes(handle));
  return [...firsts, ...rest].slice(0, 2);
};

/** An answer the study cannot give, suggesting a log line. */
const notAnswerable = (answer: string, relatesTo: string | null) =>
  JSON.stringify({
    status: 'not_answerable',
    answer,
    quotes: [],
    relates_to: relatesTo,
    suggestion: 'log',
  });

/**
 * A follow-up's answer for a stack with no model: deterministic, from the evidence alone, written
 * in the answer format and its tokens so it is read by `parseFollowUpAnswer` as a model's answer is.
 *
 * - Asked about a card whose research question went unasked or unanswered: the study cannot say,
 *   suggesting a log line.
 * - Asked about a card with a poll somebody answered: its most chosen option, and its first answer
 *   quoted, suggesting a note under that card. A card with no answered poll leads with its first
 *   theme's count, or says people answered in their own words.
 * - Asked of the whole study: how many people raised any of two themes, counted once each
 *   (`{{anyof:…}}`), suggesting a log line; with fewer than two themes, the study cannot say.
 */
export const fixtureFollowUpAnswer = (
  handles: BriefHandles,
  { scopeRqId }: { scopeRqId: string | null },
): string => {
  if (scopeRqId === null) {
    const themes = themesAcrossQuestions(handles);
    if (themes.length < 2) {
      return notAnswerable('This study has too few themes to count people across them.', null);
    }
    return JSON.stringify({
      status: 'answered',
      answer: `${followUpAnyOfToken(themes)} people raised at least one of the leading themes, each counted once. This answer was written without a model, from the evidence alone.`,
      quotes: [],
      relates_to: null,
      suggestion: 'log',
    });
  }

  const rq = handles.rqs.find((each) => each.rq_id === scopeRqId);
  if (rq === undefined) {
    throw new Error('a follow-up is scoped to a research question its handles number');
  }
  if (rq.state !== 'answered') {
    return notAnswerable(
      'Nobody answered anything about this, so the study cannot say.',
      rq.handle,
    );
  }

  const poll = answeredPoll(handles, rq);
  const [theme] = rqThemeHandles(rq);
  const lead =
    poll !== undefined
      ? pollSentence(handles, poll)
      : theme !== undefined
        ? `The leading theme here covered ${briefToken({ kind: 'count', theme: handleNumber(theme) })} people.`
        : 'People answered this in their own words.';

  return JSON.stringify({
    status: 'answered',
    answer: `${lead} This answer was written without a model, from the evidence alone.`,
    quotes: rq.quotes.slice(0, 1),
    relates_to: rq.handle,
    suggestion: 'note',
  });
};

/** The answer an "Update the brief" reviser gives, as its instructions show it. */
export const FOLLOW_UP_REVISION_EXAMPLE = `${BRIEF_ANSWER_EXAMPLE.slice(0, -1)}, "change_note": "<one or two sentences on what changed and why>"}`;

/** The sentence a revision with no model ends the sub-verdict with. */
export const FIXTURE_REVISION_SENTENCE =
  'This brief was revised after a follow-up question, without a model, from the evidence alone.';

/**
 * The Brief a stack with no model revises to from a follow-up: the Brief as it stands, in Sigora's
 * framing (`briefAnswerOf`), its sub-verdict ending with `FIXTURE_REVISION_SENTENCE`, with a fixed
 * change note. Everything else the Brief says is kept, so a revision committed since the job last
 * read the Brief survives the job's retry. A sub-verdict already ending with the sentence is kept as
 * it is, so the first update from a follow-up writes a version and a later one writes nothing, each
 * recorded — the two shapes an update can take, both reachable with no model.
 */
export const fixtureFollowUpRevision = (brief: BriefContent, handles: BriefHandles): string => {
  const answer = briefAnswerOf(brief, handles);
  const subVerdict = answer.verdict.sub_verdict.trimEnd();

  return JSON.stringify({
    ...answer,
    verdict: {
      ...answer.verdict,
      sub_verdict: subVerdict.endsWith(FIXTURE_REVISION_SENTENCE)
        ? subVerdict
        : `${subVerdict} ${FIXTURE_REVISION_SENTENCE}`.trimStart(),
    },
    change_note: 'Revised after a follow-up question, without a model.',
  });
};

/**
 * The answer format's shape for one revision: the synthesis's for the same handles
 * (`briefAnswerWireShape`) and a `change_note` beside it.
 */
export const followUpRevisionWireShape = (handles: BriefHandles) =>
  briefAnswerWireShape(handles).extend({ change_note: z.string() });

/** The wire a revision over `handles` answers under. */
export const followUpRevisionWire = (handles: BriefHandles): AnswerWire =>
  answerWire('brief_revision', followUpRevisionWireShape(handles));

/**
 * The rules a revision can break, each the reason `parseFollowUpRevision` refuses it: every rule of
 * the synthesis (`BRIEF_ANSWER_RULES`), `shape` also for a `change_note` that is missing or not a
 * string, and `change_note` for a note its words cannot be read from (`readFollowUpNote`).
 */
export const FOLLOW_UP_REVISION_RULES = [...BRIEF_ANSWER_RULES, 'change_note'] as const;

export type FollowUpRevisionRule = (typeof FOLLOW_UP_REVISION_RULES)[number];

/**
 * An "Update the brief" reviser's answer, read against the handles its prompt was numbered with: a
 * whole Brief under the synthesis's own grammar (`readBriefAnswerValue`), except that it may name
 * any kept theme, those behind "+K more" included, and a `change_note` read
 * as an answer's words are (`readFollowUpNote`), or the rule it broke (`FOLLOW_UP_REVISION_RULES`)
 * and where. A note with no words of its own is refused; nothing is rewritten.
 */
export const parseFollowUpRevision = (
  raw: string,
  handles: BriefHandles,
): AnswerReading<{ answer: BriefAnswer; note: RichText }, FollowUpRevisionRule> => {
  const document = readAnswerDocument(unfencedAnswer(raw));
  if (document === null) {
    return { ok: false, rule: 'document' };
  }
  const value = document.value;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, rule: 'shape' };
  }
  const { change_note: changeNote, ...brief } = value as Record<string, unknown>;
  if (typeof changeNote !== 'string') {
    return { ok: false, rule: 'shape', at: 'change_note' };
  }

  const answer = readBriefAnswerValue(brief, handles, { themes: 'kept' });
  if (!answer.ok) {
    return answer;
  }

  const note = readFollowUpNote(changeNote, handles);
  if (note === null) {
    return { ok: false, rule: 'change_note', at: 'change_note' };
  }

  return { ok: true, value: { answer: answer.value, note } };
};
